class ProductRecommendationsDrawer extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onTrackScroll = this.onTrackScroll.bind(this);
    this.onTrackResize = this.onTrackResize.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') {
      return;
    }

    this.dataset.initialized = 'true';

    if (this.querySelector('.upsell-product-card')) {
      this.initDrawer();
      return;
    }

    this.fetchRecommendations();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onViewportChange);

    if (this.track) {
      this.track.removeEventListener('scroll', this.onTrackScroll);
    }

    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.onViewportChange);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  async fetchRecommendations() {
    const url = this.dataset.url;

    if (!url) {
      return;
    }

    try {
      const response = await fetch(url);

      if (!response.ok) {
        return;
      }

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const drawer =
        html.querySelector('product-recommendations-drawer') ||
        html.querySelector('.product-recommendations-drawer');

      if (!drawer) {
        return;
      }

      this.innerHTML = drawer.innerHTML;

      if (this.querySelector('.upsell-product-card')) {
        this.classList.add('product-recommendations--loaded');
      }

      this.initDrawer();
    } catch (error) {
      console.error('Failed to load cart drawer recommendations', error);
    }
  }

  initDrawer() {
    this.track = this.querySelector('.product-recommendations-drawer__carousel-track');
    this.prevButton = this.querySelector('.product-recommendations-drawer__arrow--prev');
    this.nextButton = this.querySelector('.product-recommendations-drawer__arrow--next');
    this.scrollContainer = this.closest('.side-panel')?.querySelector('.side-panel-content') || null;

    if (this.querySelector('.upsell-product-card')) {
      this.classList.add('product-recommendations--loaded');
    }

    this.initCarousel();
    this.initRecommendationCards();

    document.removeEventListener('click', this.onDocumentClick);
    document.addEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onViewportChange);
    window.addEventListener('resize', this.onViewportChange);

    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.onViewportChange);
      this.scrollContainer.addEventListener('scroll', this.onViewportChange);
    }
  }

  initCarousel() {
    if (!this.track) {
      return;
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.track.scrollBy({ left: -this.track.clientWidth, behavior: 'smooth' });
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.track.scrollBy({ left: this.track.clientWidth, behavior: 'smooth' });
      });
    }

    this.track.addEventListener('scroll', this.onTrackScroll);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onTrackResize);
      this.resizeObserver.observe(this.track);
    }

    this.updateNavState();
  }

  initRecommendationCards() {
    this.querySelectorAll('.upsell-product-card').forEach((card) => {
      if (card.dataset.recommendationInitialized === 'true') {
        return;
      }

      card.dataset.recommendationInitialized = 'true';

      const variantsScript = card.querySelector('.product-recommendations-drawer__variants');
      let variants = [];

      if (variantsScript?.textContent) {
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (error) {
          console.error('Failed to parse recommendation variants', error);
        }
      }

      const selectors = Array.from(card.querySelectorAll('[data-option-index]'))
        .filter((node) => node.classList.contains('product-recommendations-drawer__selector'));

      const selectedOptions = selectors.map((selector) => {
        const trigger = selector.querySelector('[data-option-trigger]');
        return trigger?.querySelector('.product-recommendations-drawer__selector-value')?.textContent?.trim() || '';
      });

      card._recommendationState = {
        variants,
        selectors,
        selectedOptions,
      };

      selectors.forEach((selector) => {
        const trigger = selector.querySelector('[data-option-trigger]');
        const panel = selector.querySelector('[data-option-panel]');
        const optionButtons = selector.querySelectorAll('[data-option-button]');

        if (panel) {
          selector._panel = panel;
        }

        if (trigger && panel) {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const shouldOpen = panel.hasAttribute('hidden');
            this.closeAllPanels(card);
            if (shouldOpen) {
              this.openPanel(selector);
            }
          });
        }

        optionButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const optionIndex = Number(button.dataset.optionIndex);
            const optionValue = button.dataset.optionValue || '';
            this.selectOption(card, optionIndex, optionValue);
            this.closeAllPanels(card);
          });
        });
      });

      this.updateCardVariant(card);

      // Ensure recommendation forms submit via AJAX and open the cart drawer
      const recForm = card.querySelector('form');
      const recAddButton = card.querySelector('.single-add-to-cart-button');
      if (recForm && !recForm.dataset.recommendationSubmitAttached) {
        recForm.dataset.recommendationSubmitAttached = 'true';
        recForm.addEventListener('submit', (evt) => {
          evt.preventDefault();
          if (!recForm.reportValidity()) return;

          if (recAddButton) {
            recAddButton.classList.add('loading');
            recAddButton.setAttribute('aria-disabled', true);
          }

          const config = {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/javascript'
            }
          };

          const formData = new FormData(recForm);

          const sections = [];
          if (document.getElementById('Cart')) sections.push('main-cart');
          if (document.getElementById('Cart-Drawer')) sections.push('cart-drawer');
          if (document.getElementById('cart-drawer-toggle')) sections.push('cart-bubble');

          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
          config.body = formData;

          fetch(`${theme.routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                return;
              }

              // Update sections' inner HTML
              try {
                sections.forEach((section) => {
                  const sectionId = section === 'main-cart' ? 'Cart' : section === 'cart-drawer' ? 'Cart-Drawer' : 'cart-drawer-toggle';
                  const selector = section === 'main-cart' ? '.thb-cart-form' : section === 'cart-drawer' ? '.cart-drawer' : '.thb-item-count';
                  const container = document.getElementById(sectionId);
                  if (!container || !response.sections || !response.sections[section]) return;
                  const elementToReplace = container.querySelector(selector) || container;
                  elementToReplace.innerHTML = new DOMParser().parseFromString(response.sections[section], 'text/html').querySelector(selector).innerHTML;
                });

                const cartDrawer = document.getElementById('Cart-Drawer');
                if (cartDrawer) {
                  document.body.classList.add('open-cc');
                  document.body.style.overflow = 'hidden';
                  if (typeof setSidePanelInteractivity === 'function') {
                    setSidePanelInteractivity(cartDrawer, true);
                  }
                  cartDrawer.classList.add('active');
                  cartDrawer.focus();
                  dispatchCustomEvent('cart-drawer:open');
                }

                dispatchCustomEvent('cart:item-added', {
                  product: response.hasOwnProperty('items') ? response.items[0] : response
                });
              } catch (err) {
                console.error('Failed to render cart sections after recommendation add', err);
              }
            })
            .catch((e) => console.error(e))
            .finally(() => {
              if (recAddButton) {
                recAddButton.classList.remove('loading');
                recAddButton.removeAttribute('aria-disabled');
              }
            });
        });
      }
    });
  }

  onDocumentClick(event) {
    const clickedTrigger = event.target.closest('[data-option-trigger]');
    const clickedPanel = event.target.closest('[data-option-panel]');

    if (clickedTrigger && this.contains(clickedTrigger)) {
      return;
    }

    if (clickedPanel) {
      return;
    }

    this.closeAllPanels();
  }

  onTrackScroll() {
    this.updateNavState();
    this.positionOpenPanels();
  }

  onTrackResize() {
    this.updateNavState();
    this.positionOpenPanels();
  }

  onViewportChange() {
    this.positionOpenPanels();
  }

  updateNavState() {
    if (!this.track) {
      return;
    }

    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
    const scrollLeft = Math.round(this.track.scrollLeft);
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= Math.max(0, Math.round(maxScrollLeft) - 1);

    if (this.prevButton) {
      this.prevButton.disabled = atStart;
      this.prevButton.hidden = maxScrollLeft <= 1;
    }

    if (this.nextButton) {
      this.nextButton.disabled = atEnd;
      this.nextButton.hidden = maxScrollLeft <= 1;
    }
  }

  openPanel(selector) {
    const trigger = selector.querySelector('[data-option-trigger]');
    const panel = selector._panel || selector.querySelector('[data-option-panel]');

    if (!trigger || !panel) {
      return;
    }

    selector.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.dataset.floating = 'true';
    if (panel.parentElement !== document.body) {
      panel._originalParent = selector;
      document.body.appendChild(panel);
    }
    panel.removeAttribute('hidden');
    this.positionPanel(selector);
  }

  closePanel(selector) {
    const trigger = selector.querySelector('[data-option-trigger]');
    const panel = selector._panel || selector.querySelector('[data-option-panel]');

    if (!trigger || !panel) {
      return;
    }

    selector.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('hidden', '');
    if (panel._originalParent && panel.parentElement !== panel._originalParent) {
      panel._originalParent.appendChild(panel);
    }
    delete panel.dataset.floating;
    panel.style.removeProperty('top');
    panel.style.removeProperty('left');
    panel.style.removeProperty('width');
    panel.style.removeProperty('max-width');
    panel.style.removeProperty('height');
    panel.style.removeProperty('max-height');
  }

  closeAllPanels(scope = this) {
    scope.querySelectorAll('.product-recommendations-drawer__selector').forEach((selector) => {
      this.closePanel(selector);
    });
  }

  positionOpenPanels() {
    this.querySelectorAll('.product-recommendations-drawer__selector.is-open').forEach((selector) => {
      this.positionPanel(selector);
    });
  }

  positionPanel(selector) {
    const trigger = selector.querySelector('[data-option-trigger]');
    const panel = selector._panel || selector.querySelector('[data-option-panel]');

    if (!trigger || !panel || panel.hasAttribute('hidden')) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const margin = 16;
    const panelOffset = 8;
    const preferredWidth = 200;
    const preferredMaxHeight = 180;
    const availableWidth = window.innerWidth - margin * 2;
    const availableHeight = Math.max(120, window.innerHeight - margin * 2);
    const panelWidth = Math.min(preferredWidth, availableWidth);
    const panelHeight = Math.min(panel.scrollHeight, preferredMaxHeight, availableHeight);

    let left = triggerRect.left;
    let top = triggerRect.top - panelHeight - panelOffset;

    if (left + panelWidth > window.innerWidth - margin) {
      left = window.innerWidth - panelWidth - margin;
    }

    if (left < margin) {
      left = margin;
    }

    if (top < margin) {
      top = Math.min(window.innerHeight - panelHeight - margin, triggerRect.bottom + panelOffset);
    }

    panel.style.setProperty('left', `${Math.round(left)}px`, 'important');
    panel.style.setProperty('top', `${Math.round(top)}px`, 'important');
    panel.style.setProperty('width', `${Math.round(panelWidth)}px`, 'important');
    panel.style.setProperty('max-width', `${Math.round(panelWidth)}px`, 'important');
    panel.style.setProperty('height', `${Math.round(panelHeight)}px`, 'important');
    panel.style.setProperty('max-height', `${Math.round(panelHeight)}px`, 'important');
  }

  selectOption(card, optionIndex, optionValue) {
    const state = card._recommendationState;

    if (!state) {
      return;
    }

    state.selectedOptions[optionIndex] = optionValue;

    let variant = this.findMatchingVariant(state.variants, state.selectedOptions);

    if (!variant) {
      variant = state.variants.find((item) => item.options?.[optionIndex] === optionValue) || state.variants[0];
    }

    if (!variant) {
      return;
    }

    state.selectedOptions = [...variant.options];
    this.updateCardVariant(card, variant);
  }

  findMatchingVariant(variants, selectedOptions) {
    return variants.find((variant) => {
      if (!Array.isArray(variant.options)) {
        return false;
      }

      return selectedOptions.every((value, index) => variant.options[index] === value);
    });
  }

  updateCardVariant(card, variant = null) {
    const state = card._recommendationState;

    if (!state) {
      return;
    }

    const matchedVariant =
      variant ||
      this.findMatchingVariant(state.variants, state.selectedOptions) ||
      state.variants.find((item) => item.available) ||
      state.variants[0];

    if (!matchedVariant) {
      return;
    }

    state.selectedOptions = [...matchedVariant.options];

    const imageLink = card.querySelector('.product-featured-image-link');
    const image = card.querySelector('.product-primary-image');
    const titleLink = card.querySelector('.product-card-title');
    const variantInput = card.querySelector('input[name="id"]');
    const addButton = card.querySelector('.single-add-to-cart-button');

    if (imageLink && matchedVariant.url) {
      imageLink.href = matchedVariant.url;
    }

    if (titleLink && matchedVariant.url) {
      titleLink.href = matchedVariant.url;
    }

    if (variantInput) {
      variantInput.value = matchedVariant.id;
    }

    if (addButton) {
      const label = addButton.querySelector('span:not(.loading-overlay)');
      const addLabel = addButton.dataset.addLabel || 'ADD';
      const soldOutLabel = addButton.dataset.soldOutLabel || 'Sold out';
      const available = Boolean(matchedVariant.available);

      addButton.disabled = !available;

      if (label) {
        label.textContent = available ? addLabel : soldOutLabel;
      }
    }

    if (image && matchedVariant.featured_image?.src) {
      image.src = matchedVariant.featured_image.src;
      image.srcset = '';
      image.removeAttribute('sizes');
      image.alt = matchedVariant.featured_image.alt || titleLink?.textContent?.trim() || '';
    }

    state.selectors.forEach((selector, index) => {
      const value = matchedVariant.options[index];
      const trigger = selector.querySelector('[data-option-trigger]');
      const triggerValue = trigger?.querySelector('.product-recommendations-drawer__selector-value');
      const panel = selector._panel || selector.querySelector('[data-option-panel]');
      
      // Query option buttons from the panel (which may be in document.body) or selector
      let optionButtons = selector.querySelectorAll('[data-option-button]');
      if (optionButtons.length === 0 && panel) {
        optionButtons = panel.querySelectorAll('[data-option-button]');
      }

      if (triggerValue) {
        triggerValue.textContent = value;
      }

      optionButtons.forEach((button) => {
        const isSelected = button.dataset.optionValue === value;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      if (trigger?.dataset.optionType === 'color') {
        const selectedButton = Array.from(optionButtons).find((button) => button.dataset.optionValue === value);
        const selectedChip = selectedButton?.querySelector('.product-recommendations-drawer__color-chip');
        const triggerChip = trigger.querySelector('.product-recommendations-drawer__color-chip');

        if (selectedChip && triggerChip) {
          const style = selectedChip.getAttribute('style');

          if (style) {
            triggerChip.setAttribute('style', style);
          } else {
            triggerChip.removeAttribute('style');
          }
        }
      }
    });
  }
}

if (!customElements.get('product-recommendations-drawer')) {
  customElements.define('product-recommendations-drawer', ProductRecommendationsDrawer);
}
